import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { localeLabel, QuestionLanguageText } from '../surveyUtils';

interface Props {
  position: number;
  question: string;
  /** D-A15: the question in the other languages, shown under the Spanish text. */
  otherLanguages: QuestionLanguageText[];
  /** D-A12: the newest answers only (up to 10). */
  answers: string[];
  /** D-A14: the language of each answer (same order); a chip is shown when known. */
  locales: (string | null)[];
  /** D-A12: all the answers of the question in the round. */
  answerCount: number;
  onShowAll: () => void;
}

const answersLabel = (count: number): string => (count === 1 ? '1 respuesta' : `${count} respuestas`);

/**
 * A key that follows the same answer when the 15 s refresh adds newer answers at the top: the text and how many equal
 * texts are older than it (the list is newest first, so the count does not change when newer answers come in).
 */
function answerKeys(answers: string[]): string[] {
  const seen = new Map<string, number>();
  const keys = new Array<string>(answers.length);
  for (let index = answers.length - 1; index >= 0; index--) {
    const occurrence = seen.get(answers[index]) ?? 0;
    seen.set(answers[index], occurrence + 1);
    keys[index] = `${occurrence}:${answers[index]}`;
  }
  return keys;
}

/** Text answers of one question: the newest ones, in a section that folds (open by default). Plain text (React escapes it). */
export function QuestionAnswers({
  position,
  question,
  otherLanguages,
  answers,
  locales,
  answerCount,
  onShowAll,
}: Props): React.ReactElement {
  const [isOpen, setIsOpen] = useState(true);
  // D-A13: a long answer is clamped to a few lines; a click shows it whole
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggleAnswer = (key: string): void =>
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const keys = answerKeys(answers);
  const bodyId = `survey-answers-q${position}-body`;
  const hasMore = answerCount > answers.length;

  return (
    <section className='survey-results-card survey-question-answers' aria-labelledby={`survey-answers-q${position}`}>
      {/* The question stays a real heading outside the button (a button hides its children from screen readers); the
          button covers the whole header (::after), so a click anywhere on it still folds the section */}
      <header className='survey-question-answers-header'>
        <span className='survey-question-answers-badge' aria-hidden>
          {position}
        </span>
        <div className='survey-question-answers-title'>
          <h3 id={`survey-answers-q${position}`}>{question}</h3>
          {otherLanguages.map(({ locale, text }) => (
            <span key={locale} className='survey-question-answers-language'>
              <span className='survey-locale-chip'>{locale}</span>
              <span>{text}</span>
            </span>
          ))}
        </div>
        <span className='survey-question-answers-count'>
          {hasMore ? `Últimas ${answers.length} de ${answersLabel(answerCount)}` : answersLabel(answerCount)}
        </span>
        <button
          type='button'
          className='survey-question-answers-fold'
          aria-labelledby={`survey-answers-q${position}`}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          onClick={() => setIsOpen((value) => !value)}
        >
          <i className={isOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} aria-hidden />
        </button>
      </header>

      {isOpen && (
        <div id={bodyId} className='survey-question-answers-body'>
          {answers.length === 0 ? (
            <p className='survey-results-card-hint'>Nadie ha respondido a esta pregunta todavía.</p>
          ) : (
            <ul>
              {answers.map((answer, index) => (
                <li key={keys[index]} className={expanded.has(keys[index]) ? 'is-expanded' : undefined}>
                  <button type='button' aria-expanded={expanded.has(keys[index])} onClick={() => toggleAnswer(keys[index])}>
                    {locales[index] && (
                      <>
                        <span className='survey-locale-chip'>{localeLabel(locales[index])}</span>{' '}
                      </>
                    )}
                    {answer}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {hasMore && (
            <Button
              type='button'
              link
              className='survey-question-answers-toggle'
              label={`Ver las ${answerCount} respuestas en «Por persona»`}
              icon='pi pi-arrow-right'
              iconPos='right'
              onClick={onShowAll}
            />
          )}
        </div>
      )}
    </section>
  );
}
